// @ts-strict-ignore
import { EconomicsParameter } from '../../feature/editor-economics/components/economics-layout/economics-layout.component';
import { CalculatedValue } from '../../model/calculated-value';
import { ClientEvaluatedFactor } from '../../model/client-model/client-evaluated-factor';
import { ClientParameter } from '../../model/client-model/client-parameter';
import { ClientVariable } from '../../model/client-model/client-variable';
import { CarbonCausalChain, WorkOutput } from '../../model/emission-inventory-tree';
import {
  EmissionInventoryCccData,
  EmissionInventoryOutput,
  EmissionInventoryResourceData,
  FULL_PROPORTION,
} from '../../modules/shared-editor/models/emission-inventory-ccc-data';
import { EmissionParameter, Emissions } from '../../modules/shared-editor/models/emission-inventory-layout.model';
import { AccountingScope, Tree, TreeAccounting, TreeCategory } from '../../services/backend/model/tree';
import { KeyMap } from '../../services/climate-engine/ccc-algorithm/key-map';
import { CCCModelUtility } from '../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import {
  expressionIsVariableReference,
  expressionToClientEvaluatedFactor,
  extractExpression,
  findValue,
} from '../factors/factor-helpers';
import { UI_STRINGS } from '../language/uistrings';
import { formatUnitAsSnakeCase } from '../units/format-unit-as-snake-case';
import { flattenOnPropertyReducer } from '../utilities/array-utility';
import { hasPassthroughVariableValues } from '../variables/variable-helpers';

export function buildCccData(
  ccc: CarbonCausalChain,
  parameters: EmissionParameter[],
  cccModelUtility: CCCModelUtility,
  calculatedEmissionFactors: KeyMap<CalculatedValue>
): EmissionInventoryCccData {
  const resources: EmissionInventoryResourceData[] = ccc.resources.map((r) => {
    const emissionFactor: ClientParameter | CalculatedValue =
      calculatedEmissionFactors[r.resource.id] !== undefined
        ? calculatedEmissionFactors[r.resource.id]
        : parameters.find((p) => p.name === r.resource.emissionFactor?.name);
    return {
      resource: {
        id: r.resource.id,
        translationKey: r.resource.translationKey,
        nativeUnit: cccModelUtility.getResourceModel(r.resource.id)?.standardUnitOfMeasure,
        emissionFactor,
        conversionFactor: parameters.find((p) => p.name === r.resource.conversionFactor?.name),
      },
      proportion: parameters.find((p) => p.name === r.proportion) || FULL_PROPORTION,
    };
  });

  const energyIntensity: ClientEvaluatedFactor = expressionToClientEvaluatedFactor(
    ccc.energyIntensityExpression,
    ccc.energyIntensityUnit,
    parameters
  );
  const operations = parameters.find((p) => p.name === ccc.operations); // quick fix to not break for ux testing, but is required
  const operationGrowthPerCapita = parameters.find((p) => p.name === ccc.operationsGrowthPerCapita);
  const outputs = ccc.work
    ? ccc.work.outputs.map<EmissionInventoryOutput>((o) => {
        const {
          expressionFunction,
          expressionVariableNames,
          expressionParameters,
        }: {
          expressionFunction: Function;
          expressionVariableNames: string[];
          expressionParameters: ClientParameter[];
        } = extractExpression(o.workToResource.expression, parameters);
        let clientParameter: ClientParameter = undefined;
        if (
          expressionParameters?.length === 1 &&
          expressionIsVariableReference(o.workToResource.expression, expressionParameters[0].name)
        ) {
          clientParameter = expressionParameters[0];
        }
        const isNew = clientParameter && clientParameter.value === undefined;
        return {
          ...o,
          isNew,
          workToResource: {
            ...o.workToResource,
            unit: formatUnitAsSnakeCase(o.workToResource.unit),
            expressionFunction,
            expressionVariables: expressionVariableNames,
            expressionParameters,
            clientParameter,
          },
        };
      })
    : [];
  return {
    id: ccc.id,
    energyIntensity,
    translationKey: ccc.translationKey,
    operationGrowthPerCapita,
    operations,
    scope: ccc.scope,
    outputs,
    resources,
    isNew: !!(
      energyIntensity?.clientParameter?.isNew ||
      operations?.isNew ||
      operationGrowthPerCapita?.isNew ||
      resources.findIndex((r) => r.proportion?.isNew) !== -1 ||
      outputs.findIndex((o) => o.isNew) !== -1
    ),
  };
}

export function energyIntensityHasValue(energyIntensity: ClientEvaluatedFactor): boolean {
  const variable = energyIntensity?.clientParameter;
  return !!variable && !hasPassthroughVariableValues(variable.unit, variable.min, variable.max);
}

export function getCccs(tree: Tree): TreeAccounting[] {
  return tree?.categories
    ? tree.categories
        .reduce<TreeCategory[]>(
          flattenOnPropertyReducer('subCategories', (category) => !category.excludeEmissions),
          []
        )
        .filter((n) => n.accounting.length > 0)
        .map((n) => n.accounting)
        .reduce((arr, values) => {
          return [...arr, ...values];
        }, [])
    : [];
}

export function getEmissionInventoryCccData(
  tree: Tree,
  emissionParameters: EmissionParameter[],
  cccModelUtility: CCCModelUtility,
  calculatedEmissionFactors: KeyMap<CalculatedValue>
): EmissionInventoryCccData[] {
  const cccs = getCccs(tree);
  return cccs.map((ccc) => {
    const cccLeafNode: CarbonCausalChain = populateCcc(cccModelUtility, ccc);
    return buildCccData(cccLeafNode, emissionParameters, cccModelUtility, calculatedEmissionFactors);
  });
}
export function getEmissions(cccData: EmissionInventoryCccData): Emissions {
  const totalEmissions = getTotalEmissions(cccData);
  let scope1 = 0;
  let scope2 = 0;
  let scope3 = 0;
  if (cccData.scope === AccountingScope.SCOPE_1) {
    scope1 = totalEmissions;
  } else if (cccData.scope === AccountingScope.SCOPE_2) {
    scope2 = totalEmissions;
  } else if (cccData.scope === AccountingScope.SCOPE_3) {
    scope3 = totalEmissions;
  }
  return {
    totalEmissions,
    emissionsByScope: { scope1, scope2, scope3 },
  };
}

export function getEnergy(cccData: EmissionInventoryCccData): number {
  const operations = findValue(cccData.operations);
  const energyIntensity = findValue(cccData.energyIntensity); // expected to be in unit kWh/[operations unit]
  return operations * energyIntensity;
}

export function getTotalEmissions(cccData: EmissionInventoryCccData): number {
  const energy = getEnergy(cccData);
  const operations = findValue(cccData.operations);
  const emissionsFromResource = (emissionFactor: number, proportion: number, conversionFactor: number): number => {
    return energyIntensityHasValue(cccData.energyIntensity)
      ? energy * proportion * emissionFactor * conversionFactor
      : operations * proportion * emissionFactor * conversionFactor;
  };
  let total = 0;
  if (cccData.resources.length === 1) {
    total = emissionsFromResource(
      findValue(cccData.resources[0].resource.emissionFactor),
      1,
      findValue(cccData.resources[0].resource.conversionFactor) || 1
    );
  } else {
    cccData.resources.forEach((r) => {
      total += emissionsFromResource(
        findValue(r.resource.emissionFactor),
        findValue(r.proportion),
        findValue(r.resource.conversionFactor) || 1
      );
    });
  }

  return isNaN(total) ? 0 : total;
}

export function getTotalEmissionsFromInventoryTree(
  tree: Tree,
  emissionParameters: EmissionParameter[],
  cccModelUtility: CCCModelUtility,
  calculatedEmissionFactors: KeyMap<CalculatedValue>
): number {
  return getEmissionInventoryCccData(tree, emissionParameters, cccModelUtility, calculatedEmissionFactors)
    .map((cccData) => getTotalEmissions(cccData))
    .reduce((a, b) => a + b, 0);
}

export function populateCcc(cccModelUtility: CCCModelUtility, ccc: TreeAccounting) {
  const cccModel = cccModelUtility.getCccDefinitionById(ccc.ccc);
  if (!cccModel?.operation) {
    return undefined;
  }
  const cccLeafNode: CarbonCausalChain = {
    translationEnum: 'CCC',
    translationKey: ccc.ccc.toUpperCase(),
    id: ccc.ccc,
    operations: cccModel.operation.variable,
    operationsGrowthPerCapita: cccModel.operation.growthFactor?.expression,
    scope: ccc.scope,
    resources: [],
  };
  if (cccModel?.work && cccModel?.work.length === 1) {
    if (cccModel.work[0].input?.length > 0) {
      cccModel.work[0].input.forEach((input) => {
        const r = cccModelUtility.getResourceModel(input.resource);
        cccLeafNode.resources.push({
          resource: {
            id: r.resource,
            nativeUnit: r.standardUnitOfMeasure,
            translationKey: 'UI_STRINGS.MODEL.RESOURCES.' + r.resource.toUpperCase(),
            emissionFactor: {
              name: input.emissionFactor.expression,
            },
            conversionFactor: {
              name: input.resourceToWork.expression,
            },
          },
          proportion: input.resourceProportion,
        });
      });
    }
    if (cccModel.work[0].output?.length > 0) {
      const outputs: WorkOutput[] = cccModel.work[0].output.map((o) => {
        return {
          resource: {
            nativeUnit: cccModelUtility.getResourceModel(o.resource).standardUnitOfMeasure,
            translationKey: UI_STRINGS.MODEL.RESOURCES[o.resource.toUpperCase()],
          },
          primary: o.primary,
          unit: o.unitOfMeasure,
          workToResource: {
            ...o.workToResource,
            unit: o.workToResource.unitOfMeasure,
          },
        };
      });
      cccLeafNode.work = {
        outputs,
      };
    }
    if (cccModel.work[0].operationToWork.expression) {
      cccLeafNode.energyIntensity = undefined;
      cccLeafNode.energyIntensityExpression = cccModel.work[0].operationToWork.expression;
      cccLeafNode.energyIntensityUnit = formatUnitAsSnakeCase(cccModel.work[0].operationToWork.unitOfMeasure);
      cccLeafNode.energyIntensityFallback = 1;
    }
  }
  return cccLeafNode;
}

type VariableType<T> = T extends EmissionParameter
  ? EmissionParameter
  : T extends EconomicsParameter
    ? EconomicsParameter
    : never;

export function updateVariableAfterSave<T extends EconomicsParameter | EmissionParameter>(
  variableBeforeSave: VariableType<T>,
  variableAfterSave: VariableType<T>
): VariableType<T> {
  if (variableAfterSave && 'valueId' in variableAfterSave) {
    (<ClientVariable>variableBeforeSave).valueId = variableAfterSave.valueId;
  }
  if (variableAfterSave && 'id' in variableAfterSave) {
    (<ClientParameter>variableBeforeSave).id = variableAfterSave.id;
  }
  variableBeforeSave.value = variableAfterSave.value;
  variableBeforeSave.metricValue = variableAfterSave.metricValue;
  variableBeforeSave.isNew = false;
  return variableBeforeSave;
}
